import React, { useEffect, useMemo, useState } from "react";
import { Card, Badge, Spin, Empty, Divider, Button } from "antd";
import {
    ClockCircleOutlined,
    SyncOutlined,
    TruckOutlined,
    CheckOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import useQueueOrder from "api/queue/useQueueOrder";
import { OrderList } from "types/order-all";
import TimeHeader from "./components/TimeHeader";
import useOrderSummary from "api/queue/useOrderSummary";

const OrderQueueContent = () => {
    const isFullscreenMode = window.location.search.includes("fullscreen=true");

    const { data, isLoading, refetch, error } = useQueueOrder({
        shipping_date: dayjs().format("YYYY-MM-DD"),
        per_page: "100",
    });

    const { data: summaryData, refetch: refetchSummary } = useOrderSummary({
        shipping_date: dayjs().format("YYYY-MM-DD")
    });

    const [queueListState, setQueueListState] = useState<OrderList[]>([]);
    const orders = useMemo(() => data?.data?.data?.data ?? [], [data]);

    useEffect(() => {
        setQueueListState(orders);
    }, [orders]);

    const handleRefresh = () => {
        refetch();
        refetchSummary();
    };

    const verificationOrders = queueListState.filter(
        (o) => o.status === "VERIFICATION"
    );
    const processingOrders = queueListState.filter(
        (o) => o.status === "PROCESSING"
    );
    const shippingOrders = queueListState.filter((o) => o.status === "SHIPPING");
    const deliveredOrders = queueListState.filter((o) => o.status === "DELIVERED");
    const finishOrders = queueListState.filter((o) => o.status === "FINISH");

    // Grouping per tanggal dan jam
    const groupOrdersByDateTime = (orders: OrderList[]) => {
        const grouped: { [key: string]: OrderList[] } = {};
        orders.forEach((order) => {
            const dateKey = order.shipping_date || "Tanpa Tanggal";
            const timeKey = order.shipping_time || "Tanpa Waktu";
            const key = `${dateKey}|${timeKey}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(order);
        });
        return grouped;
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-100">
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-100 text-red-500">
                Terjadi kesalahan
            </div>
        );
    }

    // ✅ Komponen Section
    const TimelineSection = ({
        title,
        orders,
        color,
        icon,
    }: {
        title: string;
        orders: OrderList[];
        color: string;
        icon: React.ReactNode;
    }) => {
        const groupedOrders = groupOrdersByDateTime(orders);
        const sortedKeys = Object.keys(groupedOrders).sort();

        return (
            <Card
                title={
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">{icon}</span>
                            <span className="font-semibold text-gray-800 text-lg">{title}</span>
                        </div>
                        <Badge
                            count={orders.length}
                            showZero
                            style={{ backgroundColor: color }}
                            className="ml-auto"
                        />
                    </div>
                }
                className="shadow-sm border rounded-2xl bg-white flex flex-col h-full"
                bodyStyle={{
                    padding: "1.5rem",
                    flex: 1,
                    overflowY: "auto", // ✅ biar bisa scroll di dalam
                    maxHeight: "calc(100vh - 250px)", // ✅ atur tinggi agar pas layar
                }}
            >
                {orders.length > 0 ? (
                    <div className="space-y-5">
                        {sortedKeys.map((key) => {
                            const [date, time] = key.split("|");
                            const groupOrders = groupedOrders[key];
                            return (
                                <div key={key}>
                                    <Divider orientation="center" className="my-3 !border-gray-300">
                                        <span className="text-sm font-semibold text-gray-700">
                                            {date !== "Tanpa Tanggal"
                                                ? moment(date).format("DD MMM YYYY")
                                                : date}{" "}
                                            • {time}
                                        </span>
                                    </Divider>

                                    <div className="space-y-3">
                                        {groupOrders.map((order) => {
                                            const endTime = order.shipping_time.split(" - ")[1]; // ambil "10:00"
                                            const shippingDateTime = moment(
                                                `${order.shipping_date} ${endTime}`,
                                                "YYYY-MM-DD HH:mm"
                                            );

                                            const timelineTime = moment(order.delivered_at);
                                            const isDelivered = order.status === "FINISH";

                                            let timelineStatus: string | null = null;
                                            let timelineColor = "text-gray-500";

                                            if (isDelivered) {
                                                if (timelineTime.isAfter(shippingDateTime)) {
                                                    timelineStatus = "Terlambat";
                                                    timelineColor = "text-red-500 font-semibold";
                                                } else {
                                                    timelineStatus = "Tepat Waktu";
                                                    timelineColor = "text-green-600 font-semibold";
                                                }
                                            }


                                            return (
                                                <div
                                                    key={order.id}
                                                    className="border border-gray-200 rounded-xl p-3 shadow-sm hover:shadow-md transition bg-white"
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <div
                                                                className="font-semibold text-sm truncate max-w-[100px]"
                                                                title={order.customer_name}
                                                            >
                                                                {order.customer_name}
                                                            </div>
                                                            <p className="text-xs text-gray-500">
                                                                {order.district_name}
                                                            </p>
                                                        </div>

                                                        <div className="text-right">
                                                            <p className="text-xs text-gray-500">
                                                                Kurir:
                                                            </p>
                                                            <p className="text-sm font-medium text-gray-700">
                                                                {order.courier_name || "-"}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="mt-2 flex justify-between items-center border-t pt-2">
                                                        <span className="text-xs text-gray-500 flex-col">
                                                            <div>Waktu: </div>
                                                            <div>
                                                                {order.timeline
                                                                    ? moment(order.timeline).format("HH:mm")
                                                                    : "-"}
                                                            </div>
                                                        </span>

                                                        {timelineStatus && (
                                                            <span className={timelineColor}>
                                                                {timelineStatus}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center h-full">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span className="text-gray-500">
                                    Tidak ada pesanan {title.toLowerCase()}
                                </span>
                            }
                        />
                    </div>
                )}
            </Card>
        );
    };


    return (
        <div
            className={`bg-gray-100 min-h-screen ${isFullscreenMode ? "p-6" : "p-6"
                }`}
        >
            {/* Header */}
            <Card className="mb-6 border-none shadow-sm rounded-2xl">
                <div className="flex justify-between items-center">
                    <TimeHeader summary={summaryData?.data} />
                    <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        onClick={handleRefresh}
                        size="large"
                        className="bg-blue-600 hover:bg-blue-500 rounded-xl font-medium"
                    >
                        Refresh Data
                    </Button>
                </div>
            </Card>

            {/* Columns */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-[calc(100vh-180px)]">
                <TimelineSection
                    title="Diproses"
                    orders={processingOrders}
                    color="#1890ff"
                    icon={<SyncOutlined className="text-blue-500" spin />}
                />
                <TimelineSection
                    title="Double Check"
                    orders={verificationOrders}
                    color="#fa8c16"
                    icon={<ClockCircleOutlined className="text-orange-500" />}
                />
                <TimelineSection
                    title="Siap Dikirim"
                    orders={shippingOrders}
                    color="#52c41a"
                    icon={<CheckOutlined className="text-green-500" />}
                />
                <TimelineSection
                    title="Dikirim"
                    orders={deliveredOrders}
                    color="#52c41a"
                    icon={<TruckOutlined className="text-green-500" />}
                />
                <TimelineSection
                    title="Selesai"
                    orders={finishOrders}
                    color="#070807ff"
                    icon={<CheckCircleOutlined className="text-black" />}
                />
            </div>
        </div>
    );
};

export default OrderQueueContent;
